import { useState, ReactNode, HTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { Accordion, AccordionDetails, AccordionSummary, lighten } from '@mui/material';
import { PolarisIcon } from '@/exports/columnCommonComponents';

type TitleBrowserAccordionProps = Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  title: ReactNode;
  children: ReactNode;
  disableExpand?: boolean;
  defaultExpanded?: boolean;
  expandedCallback?: (expanded: boolean) => void;
};

const TitleBrowserAccordion = ({ title, children, disableExpand, defaultExpanded, expandedCallback, ...props }: TitleBrowserAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(!!defaultExpanded);

  const toggleAccordion = () => {
    if (expandedCallback) expandedCallback(!isExpanded);
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <AccordionWrapper {...props}>
        <Accordion
          expanded={isExpanded}
          onChange={disableExpand ? undefined : toggleAccordion}
          disabled={disableExpand}
          defaultExpanded={defaultExpanded}
          disableGutters
        >
          <AccordionSummary expandIcon={<ExpandIcon icon={'right'} />} aria-controls={`title-browser-accordion`} id={`title-browser-accordion`}>
            {title}
          </AccordionSummary>
          <AccordionDetails style={{ overflowY: 'auto' }}>{children}</AccordionDetails>
        </Accordion>
      </AccordionWrapper>
    </>
  );
};

export default TitleBrowserAccordion;

const AccordionWrapper = styled.div(({ theme }) => ({
  marginBottom: 10,
  borderRadius: theme.shape.borderRadius,
  '.MuiAccordion-root.MuiPaper-root': {
    background: theme.palette.background.paper,
    '.MuiAccordionSummary-root': {
      flexDirection: 'row-reverse',
      minHeight: 80,
      padding: theme.spacing(1, 2, 1, 0)
    }
  },
  '.Mui-expanded': {
    '> svg': {
      color: theme.palette.primary.main
    }
  },
  '.MuiCollapse-root': {
    width: '100%',
    '.MuiAccordion-root.MuiPaper-root': {
      background: lighten(theme.palette.background.paper, 0.1)
    },
    '.Mui-expanded': {
      '> svg': {
        color: theme.palette.secondary.main
      }
    }
  },
  '.MuiAccordionDetails-root': {
    paddingBottom: 10
  },
  '.MuiBox-root div': {
    marginRight: 10
  }
}));

const ExpandIcon = styled(PolarisIcon)(() => ({
  height: 25,
  width: 25,
  margin: 10
}));
